import React, { ReactNode } from 'react';
import { Button, Drawer } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { t } from '../i18n';

interface EditableDrawerProps {
    title: ReactNode;
    visible: boolean;
    onClose?: () => void;
    onSubmit?: () => void;
    width?: number;
    children: ReactNode;
    confirmText?: string;
    cancelText?: string;
    className?: string;
    confirmDisabled?: boolean;
    confirmLoading?: boolean;
    footer?: ReactNode;
    showDefaultFooter?: boolean;
    maskClosable?: boolean; // 点击遮罩层是否关闭抽屉
    zIndex?: number; // 抽屉层级，多抽屉叠加时保证后打开的在上层
    destroyOnClose?: boolean; // 关闭时销毁子元素，再次打开时状态重置
    afterOpenChange?: (open: boolean) => void; // 打开/关闭动画结束后回调（如关闭动画结束后再重置表单，避免滑出过程中闪烁）
}

/**
 * 通用的表单抽屉组件，可以用于编辑或创建各种数据
 */
const EditableDrawer: React.FC<EditableDrawerProps> = ({
    title,
    visible,
    onClose,
    onSubmit,
    width = 400,
    children,
    confirmText = t('components.button.confirm'),
    cancelText = t('components.button.cancel'),
    className,
    confirmDisabled = false,
    confirmLoading = false,
    footer,
    showDefaultFooter = true,
    maskClosable,
    zIndex,
    destroyOnClose,
    afterOpenChange,
}) => {
    const mergedClassName = [
        className,
        // Drawer 内表单 label 左对齐（用 Tailwind 的 arbitrary selector，避免写全局 CSS）
        // 使用 !important，避免被 AntD 默认样式覆盖
        "[&_.ant-form-item-label]:!text-left",
        "[&_.ant-form-item-label>label]:!justify-start",
        "[&_.ant-form-item-label>label]:!w-full",
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <Drawer
            title={
                <div className="flex items-center justify-start w-full flex-wrap">
                    {typeof title === 'string' ? (
                        <span className="text-lg font-semibold border-l-4 border-purple-600 pl-2">{title}</span>
                    ) : (
                        title
                    )}
                </div>
            }
            placement="right"
            open={visible}
            closable={false}
            onClose={onClose}
            maskClosable={maskClosable}
            className={mergedClassName}
            width={width}
            zIndex={zIndex}
            destroyOnClose={destroyOnClose}
            afterOpenChange={afterOpenChange}
            extra={
                <Button
                    type='text'
                    onClick={onClose}
                    icon={<CloseOutlined />}
                >
                </Button>
            }
            footer={
                footer || (showDefaultFooter && onSubmit ? (
                    <div className="flex justify-end space-x-4">
                        <Button
                            type="primary"
                            onClick={onSubmit}
                            loading={confirmLoading}
                            disabled={confirmDisabled || confirmLoading}
                        >
                            {confirmText}
                        </Button>
                        <Button
                            onClick={onClose}
                            type='default'
                        >
                            {cancelText}
                        </Button>
                    </div>
                ) : undefined)
            }
        >
            <div className="space-y-4 overflow-y-auto">
                {children}
            </div>
        </Drawer>
    );
};

export default EditableDrawer;
